import React from 'react';
import { call, put, select, take, race } from 'redux-saga/effects';
import type { SagaIterator } from 'redux-saga';
import { actions } from '../../Stream';
import {
  getCurrentMediaSource,
  getCurrentSubfolderId,
  getGallectionById,
  getCurrentFolderId,
  getGrusername,
} from '../selectors';
import { getGallectionStreamId, getGallectionType } from '../helpers';
import type { Action } from 'redux-actions';
import { RemoveGallectionPayload, unsetCurrentSubfolderId } from '../actions';
import { requestPuppy } from '@wix/da-http-client';
import {
  isPremiumGallection,
  getPremiumGallectionType,
} from '../../utils/gallections';
import {
  pushGlobalConfirmationModalForMM,
  confirmYes,
  confirmNo,
} from '../../Modals/redux/actionCreators';
import { TrashCan, PremiumTrash } from '@wix/da-ds/pkg/Icons/Confirmations';
import type { PapiRequestDaSharedGallectionFoldersDelete } from '@wix/da-papi-types';

function* removeGallection(
  action: Action<RemoveGallectionPayload>
): SagaIterator {
  const selectedFolderId = action.payload!;
  const mediaSource: ReturnType<typeof getCurrentMediaSource> = yield select(
    getCurrentMediaSource
  );
  const currentFolderId: ReturnType<typeof getCurrentFolderId> = yield select(
    getCurrentFolderId
  ); // when we're inside a folder and removing a sub-folder
  const selectedFolder: ReturnType<typeof getGallectionById> = yield select(
    getGallectionById,
    selectedFolderId
  );
  const username: ReturnType<typeof getGrusername> = yield select(
    getGrusername
  );

  if (!selectedFolder) {
    return;
  }

  const folderType = getPremiumGallectionType(selectedFolder) || 'regular';
  const currentSubfolderId: ReturnType<typeof getCurrentSubfolderId> =
    yield select(getCurrentSubfolderId);
  const streamId = getGallectionStreamId(mediaSource, currentFolderId);

  if (isPremiumGallection(selectedFolder)) {
    yield put(
      pushGlobalConfirmationModalForMM({
        titleTransKey:
          folderType === 'paid'
            ? 'mediapopup.confirmationPopup.removeFolder.title.paid'
            : 'mediapopup.confirmationPopup.removeFolder.title.watchers',
        textTransKey:
          folderType === 'paid'
            ? 'mediapopup.confirmationPopup.removeFolder.text.paid'
            : 'mediapopup.confirmationPopup.removeFolder.text.watchers',
        confirmBtnLabelTransKey: 'mediapopup.itemMenu.delete',
        cancelBtnLabelTransKey: 'popup.cancel',
        confirmBtnType: 'danger',
        icon: <PremiumTrash />,
      })
    );
  } else {
    yield put(
      pushGlobalConfirmationModalForMM({
        titleTransKey: `mediapopup.confirmationPopup.removeFolder.${selectedFolder.type}.title`,
        textTransKey: `mediapopup.confirmationPopup.removeFolder.${selectedFolder.type}.text`,
        confirmBtnLabelTransKey: 'mediapopup.itemMenu.delete',
        cancelBtnLabelTransKey: 'popup.cancel',
        confirmBtnType: 'danger',
        icon: <TrashCan />,
      })
    );
  }

  const { no } = yield race({
    yes: take(confirmYes),
    no: take(confirmNo),
  });
  if (no) {
    return;
  }

  yield put(actions.deleteItem({ streamId, item: selectedFolderId }));
  if (selectedFolderId === currentSubfolderId) {
    yield put(unsetCurrentSubfolderId());
  }

  const data: PapiRequestDaSharedGallectionFoldersDelete = {
    username,
    type: getGallectionType(mediaSource) ?? 'collection',
    folderid: selectedFolderId,
  };

  yield call(requestPuppy, {
    method: 'post',
    url: 'gallection/folders/delete',
    data,
  });
}

export default removeGallection;
